import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { ResourceWithIncludedResources } from 'src/careflow/helpers/types';
import {
  ConsentWrapper,
  WrappedConsent,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Consent';
import { fhirClient } from 'src/App';
import {
  ActivityDefinition,
  Bundle,
  CodeSystemConcept,
  Consent,
  FhirResource,
  Organization,
  PractitionerRole,
  Task,
  ValueSet,
} from 'src/careflow/ph-core/fhir-client/resources';
import { getPractitionerRoleRelatedResources } from 'src/careflow/care-coordination/patient/Consent/ConsentGrid';
import { getDynamicValueExpressionByPath, getReferenceId } from 'src/utils/fhir';
import { get, isArray, isEmpty, isObject, isEqual } from 'lodash';
import moment, { DurationInputArg2 } from 'moment';
import {
  ValueSetWrapper,
  WrappedValueSet,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ValueSet';
import { getParseColumn, mapDataAndDataSchemaWorkflowInstance } from './util';
import { WorkflowService } from '../api/WorkflowService';
import { getValueSetConceptValue } from 'src/careflow/helpers/common';

export const getConsents = async (
  patient: WrappedPatient | null
): Promise<ResourceWithIncludedResources<WrappedConsent>[]> => {
  if (!patient?.id) return [];
  try {
    const url = `Consent?patient=${patient.id}&_include=Consent:source-reference&_include=Consent:consentor&_include=Consent:organization&_count=1000`;
    const response = await fhirClient.search<Bundle>(url);
    if (!response?.entry?.length) return [];
    let entries = response.entry.reduce<FhirResource[]>((resources, { resource }) => {
      if (!resource) return resources;
      return [...resources, resource];
    }, []);
    const consents = entries.filter(({ resourceType }) => resourceType === 'Consent') as Consent[];

    const practitionerRoleRelatedResources = await getPractitionerRoleRelatedResources(consents);

    entries = [...entries, ...practitionerRoleRelatedResources];

    return consents.reduce<ResourceWithIncludedResources<WrappedConsent>[]>(
      (resources, consentUnwrapped) => {
        if (!consentUnwrapped?.id) return resources;
        const consent = ConsentWrapper(consentUnwrapped as Consent);

        const documentReference = entries.find?.(
          ({ id }) => id === getReferenceId(consent?.sourceReference?.reference)
        );

        const organizations =
          consent?.organization?.reduce?.<Organization[]>((organizations, { reference }) => {
            const organization = entries.find?.(
              ({ id }) => id === getReferenceId(reference)
            ) as Organization;
            if (!organization) return organizations;
            return [...organizations, organization];
          }, []) ?? [];

        const performers =
          consent?.performer?.reduce?.<FhirResource[]>((resources, { reference }) => {
            const resource = entries.find?.(({ id }) => id === getReferenceId(reference));
            if (!resource) return resources;
            return [...resources, resource];
          }, []) ?? [];

        const prRelatedResources = performers.reduce<FhirResource[]>((resources, resource) => {
          if (resource?.resourceType !== 'PractitionerRole') return resources;
          const practitionerRole = resource as PractitionerRole;

          const organization = entries.find(
            ({ id }) => id === getReferenceId(practitionerRole?.organization?.reference)
          );

          const practitioner = entries.find(
            ({ id }) => id === getReferenceId(practitionerRole?.practitioner?.reference)
          );
          return [
            ...resources,
            ...(organization ? [organization] : []),
            ...(practitioner ? [practitioner] : []),
          ];
        }, []);

        return [
          ...resources,
          {
            resource: consent,
            includedResources: [
              ...(documentReference ? [documentReference] : []),
              ...organizations,
              ...performers,
              ...prRelatedResources,
            ],
          },
        ];
      },
      []
    );
  } catch (error) {
    return [];
  }
};

export const handlerGenericStepWorkflow = async (
  workflowInstance: any,
  workflowRecord: any,
  payloadUpdateWorkflowinstance: any,
  checklistData: any,
  userEmail: string,
  modifiedFields: any = null,
  additionalParams: any = null
) => {
  const { data: dataWorkflowInstance, dataSchema } = mapDataAndDataSchemaWorkflowInstance(
    workflowInstance,
    userEmail,
    modifiedFields,
    additionalParams,
    checklistData.code
  );

  const payloadForUpdateWorkflowinstance = {
    ...payloadUpdateWorkflowinstance,
    data: dataWorkflowInstance,
    dataSchema: dataSchema,
  };

  let setCondition = JSON.parse(checklistData.conditions);
  if (setCondition?.data) {
    if (Array.isArray(setCondition.data)) {
      const manualIndex = setCondition.data.findIndex((e) => e.type === 'Manual');

      if (manualIndex !== -1) {
        setCondition.data[manualIndex] = {
          ...setCondition.data[manualIndex],
          data: {
            confirm: true,
            ...setCondition.data[manualIndex].data,
          },
        };
      }
    } else if (setCondition.type === 'Manual') {
      setCondition.data = {
        ...setCondition.data,
        confirm: true,
      };
    }
  }

  const conditions = setCondition ? JSON.stringify(setCondition) : null;

  const payloadForSetItemCompleted = {
    item: { ...checklistData, conditions },
    record: {
      ...workflowRecord,
      ...workflowInstance,
      data: dataWorkflowInstance,
      dataSchema: dataSchema,
    },
  };

  await WorkflowService.updateWorkflowInstance(payloadForUpdateWorkflowinstance);
  await WorkflowService.markItemCompleted(payloadForSetItemCompleted);
};

export const workflowInstanceData = (workflowInstance: any, pathScope: string) => {
  const workflowInstanceData = getParseColumn(workflowInstance, 'data');
  const getData =
    workflowInstanceData && Object.keys(workflowInstanceData).length > 0
      ? get(workflowInstanceData, `scope.${pathScope}`, '')
      : '';

  const data = !isEmpty(getData) ? getData : null;

  return data;
};

function getTaskDataBasedOnActivityDefinition(
  activityDefinition: ActivityDefinition,
  patient: WrappedPatient | null,
  taskPriorities: WrappedValueSet | null,
  taskIntents: WrappedValueSet | null,
  taskStatuses: WrappedValueSet | string | null
): Task {
  const priority = getValueSetConceptValue(
    taskPriorities?.asList?.() ?? [],
    activityDefinition?.priority
  )?.code;

  const intent = getValueSetConceptValue(
    taskIntents?.asList?.() ?? [],
    getDynamicValueExpressionByPath(activityDefinition?.dynamicValue, 'task.intent')
  )?.code;

  const status =
    typeof taskStatuses === 'string'
      ? taskStatuses
      : taskStatuses
      ? getValueSetConceptValue(
          taskStatuses.asList?.() ?? [],
          getDynamicValueExpressionByPath(activityDefinition?.dynamicValue, 'task.status')
        )?.code
      : null;

  const taskType = activityDefinition?.code?.coding?.[0] ?? null;

  let taskStartDate: moment.Moment | null = null;
  let taskEndDate: moment.Moment | null = null;

  let startDate: moment.Moment | null = moment();

  if (activityDefinition?.timingDuration?.value && activityDefinition?.timingDuration?.unit) {
    taskStartDate = startDate?.isValid?.() ? startDate : moment();
    const isNegativeDuration = activityDefinition.timingDuration.value < 0;

    if (!isNegativeDuration) {
      taskEndDate = taskStartDate
        .clone()
        .add(
          activityDefinition?.timingDuration?.value,
          activityDefinition?.timingDuration?.unit as DurationInputArg2
        );
    }
  }

  let payloadTask: any = {
    activityDefinitionId: activityDefinition?.id,
    patientId: patient?.id,
    patientName: patient?.getFullName(),
    ...(status ? { status: status } : {}),
    ...(taskStartDate ? { startDate: taskStartDate } : {}),
    endDate: taskEndDate,
    ...(taskType ? { taskType: taskType as CodeSystemConcept } : {}),
    ...(priority ? { priority: priority } : {}),
    ...(intent ? { intent: intent } : {}),
    ...(activityDefinition?.description
      ? {
          description:
            activityDefinition?.description ??
            activityDefinition?.title ??
            activityDefinition?.name,
        }
      : {}),
  };

  return payloadTask;
}

export async function mapActivityDefinitionTask(
  activityDefinitionId: string,
  patient: WrappedPatient | null,
  customStatus: string | null,
  includeAction?: boolean
) {
  let task;

  try {
    let activityDefinition = await fhirClient.getResource<any>(
      `/ActivityDefinition/${activityDefinitionId}`
    );

    if (!activityDefinition) return null;

    const valueSetsIds = [
      'ph-task-intent',
      'ph-task-priority',
      !customStatus ? 'ph-task-status' : null,
    ].filter(Boolean);

    const getFhirResource = await fhirClient.search<ValueSet>(`/ValueSet?_id=${valueSetsIds}`);

    const getValueSets = getFhirResource?.entry?.map((entry) => entry?.resource as ValueSet) ?? [];

    const valueSets = valueSetsIds?.map(
      (identifierValue) =>
        getValueSets.find(
          ({ identifier }) => !!identifier?.find?.(({ value }) => value === identifierValue)
        ) ?? null
    );

    const allValueSets = !customStatus ? valueSets : [undefined, ...valueSets];

    const [taskStatuses, taskIntents, taskPriorities] = allValueSets;

    task = getTaskDataBasedOnActivityDefinition(
      activityDefinition,
      patient ? patient : null,
      taskPriorities ? ValueSetWrapper(taskPriorities) : null,
      taskIntents ? ValueSetWrapper(taskIntents) : null,
      customStatus || (taskStatuses ? ValueSetWrapper(taskStatuses) : null)
    );

    return {
      ...task,
      ...(includeAction && { action: activityDefinition }),
    };
  } catch (err) {
    console.error('Failed to map task', err);
  }
}

type AssignedResource = {
  fhirReference: string;
  assignedDate: string;
  assignedBy: string;
};

type MappingChecklistItem = Record<
  string,
  {
    key: string;
    [key: string]: AssignedResource[] | AssignedResource | string;
  }
> | null;

export function mapFhirResourceToReference(
  resources: FhirResource | FhirResource[] | undefined,
  userEmail: string
) {
  if (!resources) return null;

  let mappedResources: AssignedResource[] | AssignedResource;

  if (isArray(resources)) {
    mappedResources = resources.map((resource) => ({
      fhirReference: mapFhirResourceReference(resource) ?? '',
      assignedDate: moment().toISOString(),
      assignedBy: userEmail,
    }));
  } else if (isObject(resources)) {
    mappedResources = {
      fhirReference: mapFhirResourceReference(resources) ?? '',
      assignedDate: moment().toISOString(),
      assignedBy: userEmail,
    };
  } else {
    return null;
  }

  return mappedResources;
}

export function buildMappingChecklistItem(
  itemType: string,
  key: string,
  resources: FhirResource | FhirResource[] | undefined,
  userEmail: string
): MappingChecklistItem {
  if (!resources) return null;

  const mappedResources: AssignedResource[] | AssignedResource | null = mapFhirResourceToReference(
    resources,
    userEmail
  );

  if (!mappedResources) return null;

  return {
    [itemType]: {
      key,
      [key]: mappedResources,
    },
  };
}

export const buildModifiedFieldsFromData = (inputData: any, email: string, checklistItem?: any) => {
  if (!inputData || inputData.skip) return;

  const { instantiatesCanonical, reasonData } = inputData;

  const dataToMap = Array.isArray(inputData)
    ? inputData
    : (() => {
        const { instantiatesCanonical, reasonData, ...rest } = inputData;
        return rest;
      })();

  const { key } = getParseColumn(checklistItem, 'metadata')?.ui?.data ?? {};

  const mapFhirResource = mapFhirResourceToReference(dataToMap, email);

  const mapping =
    Array.isArray(mapFhirResource) && key ? { [key]: mapFhirResource } : mapFhirResource;

  return {
    ...mapping,
    ...(instantiatesCanonical ? inputData : {}),
    ...(reasonData && { reasonData: reasonData }),
    source: 'workflow',
  };
};

export const getStatusCount = (data: any[]): any => {
  const statuses = {
    Completed: { count: 0, label: 'Completed', color: 'completed' },
    Cancelled: { count: 0, label: 'Cancelled', color: 'cancelled' },
    'Entered In Error': { count: 0, label: 'Entered In Error', color: 'enteredInError' },
  };
  for (const { status } of data) {
    if (statuses.hasOwnProperty(status)) statuses[status].count += 1;
  }
  return {
    ...statuses,
    ...{ All: { count: data?.length ?? 0, label: 'All', color: 'all' } },
  };
};

export const mapFhirResourceReference = (resource: FhirResource): string | null => {
  if (!resource) return null;
  const fhirReference = `${resource.resourceType}/${resource.id}${
    resource.meta?.versionId ? `|${resource.meta.versionId}` : ''
  }`;
  return fhirReference;
};

export function mapChecklistItems(dataMapping: any, workflowInstance: any) {
  const cleaned: any = {};

  const workflowInstanceData = getParseColumn(workflowInstance, 'data');

  for (const [key, value] of Object.entries(dataMapping) as any) {
    const itemKey = value.key;
    const itemArray = value[itemKey];
    if (Array.isArray(itemArray) && itemArray.length === 0) {
      continue;
    }

    const currentModifiedFieldsArray = get(
      workflowInstanceData,
      `scope.checklistItem.${key}.modifiedFields.${itemKey}`
    );

    const currentReferences = Array.isArray(currentModifiedFieldsArray)
      ? currentModifiedFieldsArray.map((e) => e?.fhirReference)
      : [];

    const newReferences = Array.isArray(itemArray) ? itemArray.map((e) => e?.fhirReference) : [];

    const hasChanged = !isEqual(currentReferences, newReferences);

    if (hasChanged) {
      const { key: _, ...rest } = value;
      cleaned[key] = rest;
    }
  }

  return cleaned;
}

export const stepLabel = (stepId: string, allChecklist: any) => {
  const filtered = allChecklist?.filter((item) => item.checkListId === stepId) ?? [];
  const { total, valid } = filtered.reduce(
    (acc, item) => {
      acc.total++;
      try {
        const metas = item?.isValid && JSON.parse(item.isValid);
        if (metas?.response?.isValid === true) {
          acc.valid++;
        }
      } catch {}
      return acc;
    },
    { total: 0, valid: 0 }
  );

  return { valid, total };
};

export const getActionProperties = (workflowHandler: any, action: any) => {
  const { type } = action;

  return {
    type,
    handler: (data: any) => workflowHandler[`set${type}`].handler(data),
    component: type,
  };
};

export const isChecklistItemValid = (item: any): boolean => {
  if (typeof item?.isValid !== 'string') {
    return false;
  }

  try {
    return JSON.parse(item.isValid)?.response?.isValid === true;
  } catch {
    return false;
  }
};

type Direction = 'next' | 'previous' | 'first' | 'last';

export const getStep = (steps: any[], code: string, direction: Direction) => {
  const index = steps.findIndex((step) => step.code === code);

  return {
    next: steps[index + 1],
    previous: steps[index - 1],
    first: steps[0],
    last: steps.at(-1),
  }[direction];
};

export const getStepProperties = ({ steps, step, memoWorkflowInstance, defaultStage }: any) => {
  const currentStep = steps?.findIndex((item: any) => item?.code === step?.code);
  const indexStep = currentStep + 1;
  const isLastStep = step?.order === steps?.length;

  const stageKey = `stage.${step?.code}`;
  const stageData = workflowInstanceData(memoWorkflowInstance, stageKey);
  const stageCompleted = Boolean(stageData?.closeOn);

  const isDefault = defaultStage === indexStep;

  return {
    currentStep,
    indexStep,
    isLastStep,
    stageCompleted,
    isDefault,
  };
};
